import axios from 'axios';
import {
  Patient,
  PatientDetailResponse,
  Practitioner,
  Prescription,
  PriorAuthorization,
  Insurance
} from '../types';
import exp from 'constants';

// ---------------------------
// Configurable Base URL
// ---------------------------
const API_BASE_URL = process.env.REACT_APP_API_URL;

// ---------------------------
// CSRF Token Getter
// ---------------------------
function getCSRFToken(): string | null {
  // Correctly parse the CSRF token from cookies
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const trimmedCookie = cookie.trim();
    if (trimmedCookie.startsWith('csrftoken=')) {
      return decodeURIComponent(trimmedCookie.substring('csrftoken='.length));
    }
  }
  return null;
}

// Export the function to get the CSRF token from cookies
export function getCSRFTokenFromCookie(): string | null {
  return getCSRFToken();
}

// ---------------------------
// Axios Instance
// ---------------------------
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,  // ✅ allow cookies
});

api.interceptors.request.use((config) => {
  // Always get the latest token from cookies for each request
  const csrfToken = getCSRFToken();
  if (csrfToken && config.method && !["get", "head", "options"].includes(config.method)) {
    config.headers["X-CSRFToken"] = csrfToken;
  }
  return config;
});

// Initialize CSRF token by fetching it from the server
export async function initCSRF() {
  try {
    // This will set the CSRF cookie
    await api.get("/csrf/");
    console.log("CSRF cookie initialized");
    return getCSRFToken(); // Return the token from the cookie
  } catch (error) {
    console.error("Failed to fetch CSRF token:", error);
    return null;
  }
}
// ---------------------------
// AUTH SERVICE
// ---------------------------
export const authService = {
  getCSRFCookie: async () => {
    try {
      // This will set the CSRF cookie
      await initCSRF();
    } catch (err) {
      console.error('CSRF token fetch error:', err);
    }
  },

  login: async (email: string, password: string) => {
    try {
      await authService.getCSRFCookie();

      const formData = new FormData();
      formData.append('email', email);
      formData.append('password', password);

      const loginRes = await axios.post(`${API_BASE_URL}/login`, formData, {
        withCredentials: true,
        headers: { 'X-CSRFToken': getCSRFToken() || '' },
      });

      if (loginRes.data?.error) throw new Error(loginRes.data.error);
      if (!loginRes.data?.success) throw new Error('Login failed. Please check your credentials.');

      try {
        const roleRes = await axios.get(`${API_BASE_URL}/user/role/`, { withCredentials: true });
        return { ...loginRes.data, role: roleRes.data.role, id: roleRes.data.id };
      } catch {
        console.warn('Fallback role resolution');

        const patientRes = await axios.get(`${API_BASE_URL}/patients/`);
        const foundPatient = patientRes.data.find((p: any) => p.email === email);
        if (foundPatient) return { ...loginRes.data, role: 'patient', id: foundPatient.pid };

        const practitionerRes = await axios.get(`${API_BASE_URL}/practitioners`);
        const foundProvider = practitionerRes.data.find((p: any) => p.email === email);
        if (foundProvider) return { ...loginRes.data, role: 'provider', id: foundProvider.id };

        return { ...loginRes.data, role: 'superuser', id: 'admin' };
      }
    } catch (error: any) {
      if (error.response) throw new Error(error.response.data?.error || 'Authentication failed');
      if (error.request) throw new Error('No response from server. Please try again later.');
      throw error;
    }
  },

  register: async (email: string, password: string, role: string) => {
    try {
      await authService.getCSRFCookie();

      const formData = new FormData();
      formData.append('email', email);
      formData.append('password', password);
      formData.append('role', role);

      const res = await axios.post(`${API_BASE_URL}/register`, formData, {
        withCredentials: true,
        headers: { 'X-CSRFToken': getCSRFToken() || '' },
      });

      if (res.data?.error) throw new Error(res.data.error);
      return res.data;
    } catch (error: any) {
      if (error.response) throw new Error(error.response.data?.error || 'Registration failed');
      if (error.request) throw new Error('No response from server. Please try again later.');
      throw error;
    }
  },

  logout: async () => {
    try {
      await authService.getCSRFCookie();

      const res = await axios.get(`${API_BASE_URL}/logout/`, {
        withCredentials: true,
        headers: { 'X-CSRFToken': getCSRFToken() || '' },
      });

      if (res.data?.error) throw new Error(res.data.error);
      return res.data;
    } catch (error: any) {
      if (error.response) throw new Error(error.response.data?.error || 'Logout failed');
      if (error.request) throw new Error('No response from server. Please try again later.');
      throw error;
    }
  },
};

// ---------------------------
// OTHER SERVICES
// ---------------------------
export const patientService = {
  getAllPatients: async (): Promise<Patient[]> => (await api.get('/patients/')).data,
  getPatientDetail: async (patientId: string): Promise<PatientDetailResponse> => (await api.get(`/patients/${patientId}/`)).data,
  getAssignedPatients: async (providerEmail: string): Promise<string[]> => (await api.get(`/assigned_patients/${providerEmail}/`)).data,
  deletePatient: async (patientId: string): Promise<void> => { await api.delete(`/patients/${patientId}/delete/`); },
};

export const practitionerService = {
  getAllPractitioners: async (): Promise<Practitioner[]> => (await api.get('/practitioners/')).data,
  getPractitionerDetail: async (practitionerId: number): Promise<Practitioner> => (await api.get(`/practitioners/${practitionerId}/`)).data,
};

export const prescriptionService = {
  getAllPrescriptions: async (): Promise<Prescription[]> => (await api.get('/prescriptions/')).data,
  getPatientPrescriptions: async (patientId: string): Promise<Prescription[]> => (await api.get(`/prescriptions/${patientId}/`)).data,
};

export const priorAuthService = {
  getAllPriorAuthorizations: async (): Promise<PriorAuthorization[]> => (await api.get('/prior-authorizations/')).data,
  getPatientPriorAuthorizations: async (patientId: string): Promise<PriorAuthorization[]> => (await api.get(`/prior-authorizations/${patientId}/`)).data,
  deletePriorAuth: async (id: number): Promise<void> => { await api.delete(`/prior-auth/${id}/delete/`); },
  createPriorAuth: async (priorAuth: Partial<PriorAuthorization>): Promise<PriorAuthorization> => (await api.post('/prior-authorizations/', priorAuth)).data,
  updatePriorAuthStatus: async (id: number, status: 'approved' | 'rejected', rejectionReason?: string): Promise<PriorAuthorization> => (
    await api.put(`/prior-auth/${id}/status/`, { status, rejection_reason: rejectionReason })
  ).data,
};

export const insuranceService = {
  getAllInsurance: async (): Promise<Insurance[]> => (await api.get('/insurance/')).data,
};

export const dashboardService = {
  getDashboardStats: async () => (await api.get('/dashboard-stats/')).data,
};

export const payerService = {
  getPendingAuthorizations: async (): Promise<PriorAuthorization[]> => (await api.get('/payer/pending-authorizations/')).data,
  getReviewedAuthorizations: async (): Promise<PriorAuthorization[]> => (await api.get('/payer/reviewed-authorizations/')).data,
  reviewAuthorization: async (id: number, status: 'approved' | 'rejected', rejectionReason?: string): Promise<PriorAuthorization> => (
    await api.put(`/payer/review-authorization/${id}/`, { status, rejection_reason: rejectionReason })
  ).data,
  updateEPAStatus: async (insuranceId: number, epaEnabled: boolean): Promise<any> => (
    await api.put(`/payer/update-epa-status/${insuranceId}/`, { epa_enabled: epaEnabled })
  ).data,
  getInsurancePlans: async (): Promise<Insurance[]> => (await api.get('/payer/insurance-plans/')).data,
};

export default api;